import { observer } from 'mobx-react-lite'
import React, { useContext, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { InfluencerContext } from '../../../../../contexts/InfluencerContext'
import Modal from '../../../../Modal/Modal'
import axiosService from '../../../../../utils/AxiosService'
import { toastError, toastSuccess } from '../../../../FormComponents/Toasts'
import { formatProvider } from '../../../../../utils/Helper'

export default observer(function RemoveAuthenticationModal({ modalOpen, setModalOpen, authentication }) {
  const navigate = useNavigate()
  const { id } = useParams()
  const { influencerState } = useContext(InfluencerContext)
  const [pendingConfirmation, setPendingConfirmation] = useState(null)

  const isLastAuthentication = influencerState.authentications.length <= 1

  const closeModal = () => {
    setPendingConfirmation(null)
    setModalOpen(false)
  }

  const submitRemoveAuthentication = (confirmActiveCampaignRemoval = false) => {
    axiosService.delete(`/api/project_manager/authentications/${authentication.id}`, {
      data: { confirm_active_campaign_removal: confirmActiveCampaignRemoval }
    })
    .then(({ influencer_removed }) => {
      const remaining = influencerState.authentications.filter(auth => +auth.id !== +authentication.id)
      influencerState.authentications = remaining

      if (!remaining.length) {
        toastSuccess(influencer_removed
          ? "Authentication removed. It was the influencer's only account, so the influencer was removed too."
          : 'Authentication removed')
        navigate('/project_manager/influencers')
        return
      }

      toastSuccess('Authentication removed')
      if (+id === +authentication.id) {
        navigate(`/project_manager/authentications/${remaining[0].id}/influencer`)
      }
      closeModal()
    })
    .catch(({ data }) => {
      if (data?.active_campaigns?.length) {
        setPendingConfirmation({ activeCampaigns: data.active_campaigns })
      } else {
        toastError(data?.error)
      }
    })
  }

  return (
    <Modal isOpen={modalOpen} onClose={closeModal}>
      <Modal.Header>Remove {formatProvider(authentication.provider)} Authentication</Modal.Header>
      <Modal.Body>
        <div className="w-96">
          {pendingConfirmation ? (
            <div>
              <p className="font-bold dark:text-white">This account is linked to active campaign(s):</p>
              <ul className="list-disc pl-5 my-2 dark:text-white">
                {pendingConfirmation.activeCampaigns.map(campaign => <li key={campaign.id}>{campaign.name}</li>)}
              </ul>
              <p className="dark:text-white">
                Removing it will also remove it from {pendingConfirmation.activeCampaigns.length > 1 ? 'those campaigns' : 'that campaign'}
                {isLastAuthentication ? ', and since this is the influencer\'s only account, delete the influencer profile' : ''}. Continue?
              </p>
              <div className="flex justify-between pt-4">
                <button
                  type="button"
                  className="font-bold text-md hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter dark:text-white p-2 px-4 rounded-lg"
                  onClick={() => setPendingConfirmation(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="bg-red-500 text-white rounded-lg p-2 px-4 text-center"
                  onClick={() => submitRemoveAuthentication(true)}
                >
                  Yes, remove it anyway
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="font-bold dark:text-white">
                Are you sure you want to remove this {formatProvider(authentication.provider)} account ({authentication.username})?
              </p>
              {isLastAuthentication && (
                <p className="mt-2 font-bold text-red-500">
                  This is the influencer's only account. Removing it will also delete the influencer profile.
                </p>
              )}
              <div className="flex justify-between pt-4">
                <button
                  type="button"
                  className="font-bold text-md hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter dark:text-white p-2 px-4 rounded-lg"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="bg-red-500 text-white rounded-lg p-2 px-4 text-center"
                  onClick={() => submitRemoveAuthentication(false)}
                >
                  Remove
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
})
